import { gql } from '@apollo/client';

/* ── MF Alpha engine operations ─────────────────────────────── */

/**
 * These three change state the whole platform reads from, and all three return
 * before any work is done: the resolver hands off to a worker thread inside the
 * API process and answers immediately. So none of them can be awaited for a
 * result — the evidence that something happened is `mfAlphaBacktestRuns`
 * gaining a row, or `mfAlphaStatus` moving.
 *
 * That worker shares the process, so while one is running the API has little
 * left for anything else and ordinary reads time out. A poll that fails during
 * a run is a busy server, not a dead one.
 */

/**
 * Walk the ranking forward over a historical window and measure it.
 *
 * The window bounds the REBALANCE dates, not the measurement: a 12m run ending
 * 2015-12-31 measures forward a full year past that, so NAV data has to extend
 * beyond the end date by the horizon or there is nothing to score. Minutes to
 * run; `includeReturns` is what produces the growth curve and every CAGR, and
 * `includePicks` stores each selection (~2,200 rows on a five-year run).
 */
export const RUN_ALPHA_BACKTEST = gql`
  mutation RunAlphaBacktest(
    $horizon: String!
    $windowFrom: String!
    $windowTo: String!
    $topFraction: Float
    $includeReturns: Boolean
    $includePicks: Boolean
    $decayLags: [Int!]
    $notes: String
  ) {
    runMfAlphaBacktest(
      horizon: $horizon
      windowFrom: $windowFrom
      windowTo: $windowTo
      topFraction: $topFraction
      includeReturns: $includeReturns
      includePicks: $includePicks
      decayLags: $decayLags
      notes: $notes
    ) {
      success
      message
    }
  }
`;

/**
 * Search for a better weight set — a diagnostic, not a tuner.
 *
 * The validation window must start on or after the fit window ends; the service
 * refuses an overlapping pair outright, because weights may only be chosen from
 * data that had already closed. `activate` is a request, not an instruction:
 * the live weights are only replaced if the proposal beats the incumbent on the
 * validation window by at least 1.0 lift. A rejected proposal is still stored,
 * and that record is the evidence the current weights were re-checked and held.
 */
export const CALIBRATE_ALPHA_WEIGHTS = gql`
  mutation CalibrateAlphaWeights(
    $horizon: String!
    $fitFrom: String!
    $fitTo: String!
    $validateFrom: String!
    $validateTo: String!
    $samples: Int
    $topFraction: Float
    $activate: Boolean
    $notes: String
  ) {
    calibrateMfAlphaWeights(
      horizon: $horizon
      fitFrom: $fitFrom
      fitTo: $fitTo
      validateFrom: $validateFrom
      validateTo: $validateTo
      samples: $samples
      topFraction: $topFraction
      activate: $activate
      notes: $notes
    ) {
      success
      message
    }
  }
`;

/**
 * Re-rank the whole equity universe against the latest NAV date.
 *
 * A fallback, not the normal path — rankings are rebuilt automatically after
 * every NAV load. It changes no weights and creates no run, so the only sign it
 * worked is `mfAlphaStatus` reporting a later ranking date.
 */
export const REGENERATE_ALPHA_SIGNALS = gql`
  mutation RegenerateAlphaSignals {
    regenerateMfAlphaSignals {
      success
      message
    }
  }
`;
